/**
 * Hooks for organization management.
 *
 * useOrgs — list all organizations
 * useCreateOrg — create new organization
 * useUpdateOrg — update org details (name, short_name)
 * useDeactivateOrg — deactivate organization
 * useUpdateCapabilities — update org feature toggles
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import { orgsAPI } from '@/api/orgs'
import type { OrgCreatePayload, OrgUpdatePayload, OrgCapabilitiesUpdatePayload } from '@/api/orgs'

export const useOrgs = () =>
  useQuery({
    queryKey: ['orgs'],
    queryFn: orgsAPI.list,
  })

export const useCreateOrg = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: OrgCreatePayload) => orgsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgs'] })
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
      message.success('Организация создана')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка создания организации')
    },
  })
}

export const useUpdateOrg = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ orgId, data }: { orgId: string; data: OrgUpdatePayload }) =>
      orgsAPI.update(orgId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgs'] })
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
      message.success('Организация обновлена')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка обновления организации')
    },
  })
}

export const useDeactivateOrg = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (orgId: string) => orgsAPI.deactivate(orgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgs'] })
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
      message.success('Организация деактивирована')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка деактивации организации')
    },
  })
}

export const useUpdateCapabilities = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ orgId, data }: { orgId: string; data: OrgCapabilitiesUpdatePayload }) =>
      orgsAPI.updateCapabilities(orgId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgs'] })
      message.success('Настройки функций обновлены')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка обновления настроек')
    },
  })
}
